import { cx } from "@jugl-web/utils";
import {
  CSSProperties,
  ElementType,
  forwardRef,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from "react";

export type InteractiveContainerNativeProps = Omit<
  HTMLAttributes<HTMLElement>,
  "as" | "onClick"
>;

export interface InteractiveContainerProps
  extends InteractiveContainerNativeProps {
  children?: ReactNode;
  as?: ElementType;
  isDisabled?: boolean;
  className?: string;
  style?: CSSProperties;
  isTextSelectable?: boolean;
  onClick?: (
    event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
  ) => void;
}

export const InteractiveContainer = forwardRef<
  HTMLElement,
  InteractiveContainerProps
>(
  (
    {
      children,
      as: Component = "div",
      tabIndex,
      isDisabled,
      className,
      style,
      isTextSelectable,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = (event: MouseEvent<HTMLElement>) => {
      if (!isDisabled) {
        onClick?.(event);
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      if (!isDisabled && event.key === "Enter") {
        onClick?.(event);
      }
    };

    return (
      <Component
        ref={ref}
        role={!isTextSelectable ? "button" : undefined}
        tabIndex={tabIndex ?? (isDisabled ? -1 : 0)}
        className={cx(
          !isDisabled && "cursor-pointer",
          isTextSelectable ? "select-text" : "select-none",
          className
        )}
        style={style}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
